// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TargetFigure from "./TargetFigure";

// Types
import {
  AniseikoniaMeridianKeyType,
  AniseikoniaMeridianResultType,
  AniseikoniaMeridianSummaryType,
  AniseikoniaTargetGeometryType,
} from "src/services/exams/types";

// Utils
import { duration, largerEyeWord } from "./aniseikoniaHelpers";

// The figure is drawn small enough that three of them fit across one card on a laptop.
const FIGURE_SIZE = 132;

type DirectionColumnType = {
  meridian: AniseikoniaMeridianKeyType;
  title: string;
  // What this direction is, in the words a patient would use.
  plainName: string;
  summary: AniseikoniaMeridianSummaryType;
  result: AniseikoniaMeridianResultType | undefined;
  geometry: AniseikoniaTargetGeometryType | undefined;
  significantAt: number;
};

/**
 * One of the three directions, as a column of a single card rather than a card of its own.
 *
 * Each column carries the size difference as a sentence, how long the direction took, the
 * spread across matches when there was more than one, and the picture the patient was
 * actually looking at when they confirmed.
 *
 * It deliberately says nothing about significance in words. The scale above the columns shades
 * the band where a reading matters, and saying it a second time here made the card wordy
 * without telling the reader anything the picture had not already told them.
 */
const DirectionColumn = ({
  meridian,
  title,
  plainName,
  summary,
  result,
  geometry,
  significantAt,
}: DirectionColumnType) => {
  const { t } = useTranslation();

  const significant = Math.abs(summary.percent) >= significantAt;

  // The last match the patient actually made, which is the closest thing to "what they ended
  // up looking at". A derived direction has none.
  const lastSetting = result?.settings
    ?.filter((setting) => setting.view)
    ?.pop();

  // The range the matches fell in, phrased so no end of it loses its eye. Unsigning a range
  // would turn "2.3% left to 4.3% right" into "2.3% to 4.3%", which describes a patient who
  // answered consistently -- the opposite of what happened.
  const spread = (() => {
    if (!summary.ci || (result?.settingCount ?? 0) < 2) return null;

    const [low, high] = summary.ci;
    const lowEye = largerEyeWord(low);
    const highEye = largerEyeWord(high);
    const matches = t("{{count}} matches", {
      count: result?.settingCount ?? 0,
    });

    if (lowEye && lowEye === highEye) {
      return t("{{matches}}: {{low}}% to {{high}}%, both {{eye}} larger", {
        matches,
        low: Math.min(Math.abs(low), Math.abs(high)).toFixed(1),
        high: Math.max(Math.abs(low), Math.abs(high)).toFixed(1),
        eye: t(lowEye),
      });
    }

    return t("{{matches}}: {{low}}% {{lowEye}} to {{high}}% {{highEye}}", {
      matches,
      low: Math.abs(low).toFixed(1),
      lowEye: t(lowEye ?? "neither"),
      high: Math.abs(high).toFixed(1),
      highEye: t(highEye ?? "neither"),
    });
  })();

  const largerEye = largerEyeWord(summary.percent);

  return (
    <Box
      data-testid={`aniseikonia-direction-${meridian}`}
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        border: "1px solid #5B6D7A40",
        borderRadius: "4px",
        padding: "8px",
      }}
    >
      <Typography
        fontSize="12px"
        fontWeight={700}
        color="#5B6D7A"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
      >
        {title}
      </Typography>

      <Typography fontSize="12px" color="#5B6D7A">
        {plainName}
      </Typography>

      <Typography
        fontSize="18px"
        fontWeight={700}
        color={significant ? "#E2772E" : "#202338"}
        sx={{ lineHeight: 1.35 }}
      >
        {largerEye
          ? t("{{percent}}% larger in the {{eye}} eye", {
              percent: Math.abs(summary.percent).toFixed(1),
              eye: t(largerEye),
            })
          : t("The same size in both eyes")}
      </Typography>

      {/* The only description a direction still carries: whether it is measurable at all.
          `detectable` is the exam's own classification against the detection floor, so this
          never re-derives a clinical cut-off from a number. */}
      {summary.detectable === false && (
        <Typography fontSize="12px" color="#5B6D7A">
          {t("Too small to measure reliably")}
        </Typography>
      )}

      <Typography fontSize="12px" color="#5B6D7A">
        {t("Duration")}: {duration(summary.durationSeconds)}
      </Typography>

      {/* Only when there was more than one match. Saying "measured once" on a section nobody
          can order differently invites the question of whether it could have been measured
          more. */}
      {spread && (
        <Typography fontSize="12px" color="#5B6D7A">
          {spread}
        </Typography>
      )}

      {lastSetting?.view && (
        <Box sx={{ mt: "4px" }}>
          <Typography
            fontSize="12px"
            color="#5B6D7A"
            sx={{ textTransform: "uppercase", mb: "4px" }}
          >
            {t("What the Patient Set")}
          </Typography>
          <TargetFigure
            geometry={geometry}
            view={lastSetting.view}
            meridian={meridian}
            size={FIGURE_SIZE}
          />
        </Box>
      )}
    </Box>
  );
};

export default DirectionColumn;
